import React from 'react';
import { Calendar, Trophy, Trash, Settings, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { TrackImage } from '../../hooks/useEventManagement';
import { formatCurrency } from '../../lib/currencyUtils';

interface EventImageGridProps {
  trackImages: TrackImage[];
  openEventDatesModal: (image: TrackImage) => void;
  openEventModal: (image: TrackImage) => void;
  handleDeleteTrackImage: (image: TrackImage) => void;
  hasExistingEvent: (image: TrackImage) => boolean;
  formatDateForDisplay: (date: string) => string;
  openTicketSettingsModal?: (eventId: string) => void;
  openTicketShopUrlModal?: (eventId: string) => void;
}

const EventImageGrid: React.FC<EventImageGridProps> = ({
  trackImages,
  openEventDatesModal,
  openEventModal,
  handleDeleteTrackImage,
  hasExistingEvent,
  formatDateForDisplay,
  openTicketSettingsModal,
  openTicketShopUrlModal
}) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {trackImages.map((image) => {
        const userRegistrations = [];
        const isRegistered = false;
        const registrationCount = 0;
        const eventIsUpcoming = true;

        return (
          <div
            key={image.id}
            className="bg-zinc-900 rounded-lg overflow-hidden relative"
          >
            {/* Event Status Badges */}
            <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
              {/* Past Event Badge */}
              {!eventIsUpcoming && (
                <div className="bg-gray-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                  Past Event
                </div>
              )}
            </div>

            {/* Event Flyer - Reduced height */}
            {image.url && (
              <div className="aspect-[16/9] relative">
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {image.is_event_flyer && (
                    <>
                      <button
                        onClick={() => openEventDatesModal(image)}
                        className="p-2 bg-blue-600 rounded-full hover:bg-blue-700"
                        title="Manage Event Dates"
                      >
                        <Calendar className="h-5 w-5 text-white" />
                      </button>
                      {/* Always show Create Event button for event flyers with dates */}
                      {image.event_dates && image.event_dates.length > 0 && (
                        <button
                          onClick={() => openEventModal(image)}
                          className="p-2 bg-green-600 rounded-full hover:bg-green-700"
                          title={image.track_type === 'drift' ? 'Create Drift Event' : 'Add Racing Category'}
                        >
                          <Trophy className="h-5 w-5 text-white" />
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => handleDeleteTrackImage(image)}
                    className="p-2 bg-primary-600 rounded-full hover:bg-primary-700"
                    title={hasExistingEvent(image) ? "Delete Event" : "Delete Image"}
                  >
                    <Trash className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>
            )}

            {/* Event Info - More compact */}
            <div className="p-4">
              <h2 className="text-xl font-bold text-white mb-2">{image.title}</h2>
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{image.description}</p>

              <div className="flex flex-wrap gap-2 mb-3">
                {image.is_featured && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Featured
                  </span>
                )}
                {image.is_event_flyer && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Event Flyer
                  </span>
                )}
                {hasExistingEvent(image) && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                    Has Events
                  </span>
                )}
                {image.event_dates && image.event_dates.length > 0 && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    {image.event_dates.length} Date{image.event_dates.length > 1 ? 's' : ''}
                  </span>
                )}
                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                  {image.track_type === 'drag' ? 'Drag' : 'Drift'}
                </span>
              </div>
              
              {/* Show event info if exists */}
              {hasExistingEvent(image) && (
                <div className="mt-2 p-2 bg-zinc-800 rounded">
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-green-400 font-medium">Events Created</p>
                    <div className="flex gap-1">
                      {image.event && openTicketShopUrlModal && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openTicketShopUrlModal(image.event.id);
                          }}
                          className="p-1 bg-zinc-700 rounded hover:bg-zinc-600"
                          title="Ticket Shop URL"
                        >
                          <LinkIcon className="h-3 w-3 text-gray-300" />
                        </button>
                      )}
                      {image.event && openTicketSettingsModal && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openTicketSettingsModal(image.event.id);
                          }}
                          className="p-1 bg-zinc-700 rounded hover:bg-zinc-600"
                          title="Ticket Settings"
                        >
                          <Settings className="h-3 w-3 text-gray-300" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    {image.event ? formatCurrency(image.event.default_registration_fee || 0) : ''}
                  </p>
                  {image.event?.ticket_shop_url && (
                    <div className="mt-1 flex items-center gap-1">
                      <ExternalLink className="h-3 w-3 text-blue-400" />
                      <p className="text-xs text-blue-400 truncate">
                        {image.event.ticket_shop_url.length > 30 
                          ? `${image.event.ticket_shop_url.substring(0, 30)}...` 
                          : image.event.ticket_shop_url}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Show event dates */}
              {image.event_dates && image.event_dates.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Blocked Dates:</p>
                  <div className="flex flex-wrap gap-1">
                    {image.event_dates.map((eventDate) => (
                      <span 
                        key={eventDate.id}
                        className="px-2 py-1 text-xs bg-zinc-700 text-gray-300 rounded"
                      >
                        {formatDateForDisplay(eventDate.event_date)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EventImageGrid;